import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./App.css";
import "./i18n";
import { trackPageView } from "./lib/analytics";
import Header from "./components/Header";
import Hero from "./components/Hero";
import TrustedBy from "./components/TrustedBy";
import About from "./components/About";
import Services from "./components/Services";
import Methodology from "./components/Methodology";
import WhyMe from "./components/WhyMe";
import FAQ from "./components/FAQ";
import CTA from "./components/CTA";
import Footer from "./components/Footer";
import SEOHead from "./components/SEOHead";
import SectionTracker from "./components/SectionTracker";
import ConsentBanner from "./components/ConsentBanner";
import LegalPage from "./components/LegalPage";
import Preloader from "./components/Preloader";
import { Toaster } from "./components/ui/sonner";

// Main page component with all sections
const MainPage = ({ lang }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const targetLang = lang || 'en';
    
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
    
    document.documentElement.lang = targetLang;
    document.documentElement.dir = targetLang === 'ar' ? 'rtl' : 'ltr';
  }, [lang, i18n]);

  // Scroll to a section if the user arrived from another page with a hash (e.g. /#services)
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash;
      const timer = setTimeout(() => {
        const el = document.querySelector(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const isRTL = i18n.language === 'ar';

  return (
    <div className={`min-h-screen bg-black ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SEOHead />
      <SectionTracker />
      <Header />
      <main>
        <Hero />
        <TrustedBy />
        <About />
        <Services />
        <Methodology />
        <WhyMe />
        <FAQ />
        <CTA />
      </main>
      <Footer />
      <Toaster position="bottom-right" />
    </div>
  );
};

// Wrapper to extract lang param
const LangPage = () => {
  const { lang } = useParams();
  return <MainPage lang={lang} />;
};

// Sends a GA4 page_view on SPA route changes (initial load is covered by gtag config)
const RouteTracker = () => {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    trackPageView(location.pathname);
  }, [location]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <Preloader />
      <BrowserRouter>
        <RouteTracker />
        <ConsentBanner />
        <Routes>
          {/* Legal pages (per language) */}
          <Route path="/privacy" element={<LegalPage type="privacy" lang="en" />} />
          <Route path="/terms" element={<LegalPage type="terms" lang="en" />} />
          <Route path="/fr/privacy" element={<LegalPage type="privacy" lang="fr" />} />
          <Route path="/fr/terms" element={<LegalPage type="terms" lang="fr" />} />
          <Route path="/es/privacy" element={<LegalPage type="privacy" lang="es" />} />
          <Route path="/es/terms" element={<LegalPage type="terms" lang="es" />} />
          <Route path="/ar/privacy" element={<LegalPage type="privacy" lang="ar" />} />
          <Route path="/ar/terms" element={<LegalPage type="terms" lang="ar" />} />
          {/* Language-specific routes for SEO */}
          <Route path="/fr" element={<MainPage lang="fr" />} />
          <Route path="/es" element={<MainPage lang="es" />} />
          <Route path="/ar" element={<MainPage lang="ar" />} />
          {/* Default English route */}
          <Route path="/" element={<MainPage lang="en" />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
